// src/hooks/useRouteContext.js — parses the current URL into a
// `{course_code, domain, concept, surface}` shape that the tutor agent
// can use as context. Lets the agent answer "explain this" without
// asking the user to recap the page they're on.
//
// Surface taxonomy:
//   landing         — /
//   my-courses      — /my-courses
//   tutor           — /tutor (legacy)
//   create-course   — /create-course
//   course          — /course/:code
//   course-manage   — /course/:code/manage
//   concept-editor  — /course/:code/manage/:domain/:concept
//   domain-content  — /course/:code/content/:domain
//   quiz            — /course/:code/quiz/:domain (and /quick-quiz)
//   quiz-results    — /course/:code/quiz/:domain/results/:quizId
//   sandbox         — /course/:code/sandbox/:domain
//   mastery         — /course/:code/mastery
//   insights        — /course/:code/insights
//   practice        — /course/:code/practice
//   unknown         — anything else
import { useLocation } from 'react-router-dom';

export function useRouteContext() {
  const { pathname } = useLocation();
  return parsePath(pathname);
}

const TOP_LEVEL_SURFACES = {
  '': 'landing',
  'my-courses': 'my-courses',
  'tutor': 'tutor',
  'create-course': 'create-course',
};

function parseManageSubpath(segs, course_code) {
  if (segs.length === 3) return { surface: 'course-manage', course_code };
  if (segs.length >= 5) {
    return {
      surface: 'concept-editor',
      course_code,
      domain: decodeURIComponent(segs[3]),
      concept: decodeURIComponent(segs[4]),
    };
  }
  return null;
}

function parseQuizSubpath(segs, course_code) {
  if (!segs[3]) return null;
  const domain = decodeURIComponent(segs[3]);
  if (segs[4] === 'results') return { surface: 'quiz-results', course_code, domain };
  return { surface: 'quiz', course_code, domain };
}

function parseBuildPath(segs) {
  if (segs.length === 1) return { surface: 'build-home' };
  return { surface: 'build-project' };
}

function parseCoursePath(segs) {
  const course_code = decodeURIComponent(segs[1]);
  if (segs.length === 2) return { surface: 'course', course_code };
  const sub = segs[2];
  if (sub === 'manage') return parseManageSubpath(segs, course_code) || { surface: 'course', course_code };
  if (sub === 'content' && segs[3]) {
    return { surface: 'domain-content', course_code, domain: decodeURIComponent(segs[3]) };
  }
  if (sub === 'quiz') return parseQuizSubpath(segs, course_code) || { surface: 'course', course_code };
  if (sub === 'quick-quiz') return { surface: 'quiz', course_code };
  if (sub === 'workbench' && segs[3]) {
    return { surface: 'workbench', course_code, domain: decodeURIComponent(segs[3]) };
  }
  if (sub === 'mastery') return { surface: 'mastery', course_code };
  if (sub === 'insights') return { surface: 'insights', course_code };
  if (sub === 'practice') return { surface: 'practice', course_code };
  return { surface: 'course', course_code };
}

// Pure function — exported so it can be unit-tested without a router.
export function parsePath(pathname) {
  const segs = (pathname || '/').split('/').filter(Boolean);
  if (segs.length === 0) return { surface: 'landing' };
  const top = TOP_LEVEL_SURFACES[segs[0]];
  if (top) return { surface: top };
  if (segs[0] === 'course' && segs[1]) return parseCoursePath(segs);
  if (segs[0] === 'build') return parseBuildPath(segs);
  return { surface: 'unknown' };
}
